<template>
  <div class="calc-card">
    <div class="calc-header">
      <div class="calc-title">설문 주문하기</div>
      <div class="calc-live-badge"><span class="calc-live-dot"></span>실시간 계산</div>
    </div>

    <div class="calc-body">
      <div class="calc-field">
        <div class="calc-field-row">
          <label class="calc-label">필요한 응답 수</label>
          <div class="calc-value">{{ headCountLabel }}</div>
        </div>
        <input
          type="range"
          class="calc-slider"
          min="0"
          :max="headCountList.length - 1"
          step="1"
          v-model.number="headCountIdx"
        >
        <div class="calc-hint-row">
          <span class="calc-hint">한 번에 최대 200명까지 받을 수 있어요</span>
          <span class="calc-hint">30 – 200</span>
        </div>
      </div>

      <div class="calc-field">
        <label class="calc-label">응답 마감 일정</label>
        <input
          type="datetime-local"
          class="calc-datetime"
          v-model="endDateTime"
          :min="minDateTime"
          :max="maxDateTime"
          step="60"
        >
        <div class="calc-hint" :class="{ 'calc-hint-warn': timeOptionCal === 6 }">
          {{ deadlineHintText }}
        </div>
      </div>

      <div class="calc-field-grid">
        <div class="calc-field">
          <label class="calc-label">설문 소요 시간</label>
          <select class="calc-select" v-model="spendTime">
            <option v-for="opt in spendTimeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="calc-field">
          <label class="calc-label">응답자 성별</label>
          <select class="calc-select" v-model="targetGender">
            <option value="ALL">성별 무관</option>
            <option value="MALE">남성</option>
            <option value="FEMALE">여성</option>
          </select>
        </div>
        <div class="calc-field">
          <label class="calc-label">주문자 신분</label>
          <select class="calc-select" v-model="identity">
            <option value="NONE">일반</option>
            <option value="UNDERGRADUATE">대학생</option>
            <option value="GRADUATE">대학원생</option>
            <option value="MID_HIGH">중/고등학생</option>
          </select>
        </div>
      </div>

      <div class="calc-field">
        <label class="calc-label">응답자 연령</label>
        <div class="age-list">
          <label class="age-item">
            <input type="checkbox" :checked="ageAll" @change="selectAgeAll">
            연령 무관
          </label>
          <label
            v-for="opt in ageOptions"
            :key="opt.value"
            class="age-item"
          >
            <input
              type="checkbox"
              :checked="!ageAll && targetAgeOptionList.includes(opt.value)"
              @change="toggleAge(opt.value)"
            >
            {{ opt.label }}
          </label>
        </div>
        <div class="calc-hint">연령 무관이면 배수 없이 기본 가격으로 진행돼요</div>
      </div>

      <label class="calc-checkbox-row">
        <input type="checkbox" v-model="enTarget">
        영어 설문이에요 (50명까지)
      </label>

      <div class="price-breakdown">
        <div class="price-row">
          <span>
            기본 금액
            <span class="price-row-note">{{ baseAmountNote }}</span>
          </span>
          <span>{{ priceToString(baseAmount) }}원</span>
        </div>
        <div class="price-row">
          <span>응답 마감 추가 금액</span>
          <span>+{{ priceToString(extraFee) }}원</span>
        </div>
        <div class="price-divider"></div>
        <div class="price-row price-row-strong">
          <span>주문 금액</span>
          <span>{{ priceToString(calOrderPrice) }}원</span>
        </div>
        <div class="price-row" v-if="discountAmount > 0">
          <span>{{ identityDiscountLabel }}</span>
          <span class="price-discount">-{{ priceToString(discountAmount) }}원</span>
        </div>
        <div class="price-divider"></div>
        <div class="price-row price-final">
          <span>결제 금액</span>
          <span>{{ priceToString(calculate) }}원</span>
        </div>
      </div>

      <button type="button" class="calc-order-btn" @click="nextPage">
        이 조건으로 설문 주문하기 <span class="calc-order-arrow">→</span>
      </button>
      <div class="calc-subtext" v-if="!hideSubtext">가입 없이 견적 확인</div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { showAlert } from '@/plugins/dialog'
import { requireLogin } from '@/plugins/auth-modal'

export default {
  name: 'HomeOrderCalculator',
  props: {
    // 모달(가입 유도 문구가 필요 없는 맥락)에서 재사용할 때 하단 안내문 숨김
    hideSubtext: {
      type: Boolean,
      default: false
    }
  },
  // 주문(라우팅)이 실제로 성사됐을 때 — 모달이 같은 라우트로 다시 push되는
  // 경우(주문 상세 페이지 안에서 옵션 수정) $route watch만으로는 안 닫혀서 필요
  emits: ['ordered'],
  data() {
    return {
      // ServiceOption.vue 와 동일한 13단계 응답 수 (스냅 슬라이더)
      headCountList: [30, 40, 50, 60, 70, 80, 90, 100, 120, 140, 160, 180, 200],
      headCountIdx: 7, // 기본값 100명

      spendTime: 'TIME_1_3',
      spendTimeOptions: [
        { value: 'TIME_0', label: '1분 이내 (5문항 내외)' },
        { value: 'TIME_1_3', label: '1-3분 (12문항 이내)' },
        { value: 'TIME_4_6', label: '4-6분 (25문항 이내)' },
        { value: 'TIME_7_10', label: '7-10분 (50문항 이내)' },
        { value: 'TIME_11_15', label: '11-15분 (100문항 이내)' },
        { value: 'TIME_16_20', label: '16-20분 (100문항 이상)' }
      ],

      endDate: '',
      endTime: '12:00',
      nowDate: new Date(),

      targetGender: 'ALL',
      identity: 'UNDERGRADUATE',
      enTarget: false,

      ageAll: true,
      targetAgeOptionList: [],
      ageOptions: [
        { value: 'AGE_20_24', label: '20-24세' },
        { value: 'AGE_25_29', label: '25-29세' },
        { value: 'AGE_30_34', label: '30-34세' },
        { value: 'AGE_35_39', label: '35-39세' },
        { value: 'AGE_40_44', label: '40-44세' },
        { value: 'AGE_45_49', label: '45-49세' },
        { value: 'AGE_50_59', label: '50대' },
        { value: 'AGE_60_69', label: '60대' }
      ],

      // 아래 가격표·배율은 ServiceOption.vue 원본을 그대로 복제한 것이다. 값을 바꾸지 않는다.
      priceTable:
        [ [9900, 9900, 13200, 16500, 19800, 23100, 26400, 29700, 33000, 39600, 46200, 52800, 59400, 66000],
  [9900, 9900, 13200, 16500, 19800, 23100, 26400, 29700, 33000, 39600, 46200, 52800, 59400, 66000],
  [29700, 29700, 39600, 49500, 59400, 69300, 79200, 89100, 99000, 118800, 138600, 158400, 178200, 198000],
  [59400, 59400, 79200, 99000, 118800, 138600, 158400, 178200, 198000, 237600, 277200, 316800, 356400, 396000],
  [99000, 99000, 132000, 165000, 198000, 231000, 264000, 297000, 330000, 396000, 462000, 528000, 594000, 660000],
  [148500, 148500, 198000, 247500, 297000, 346500, 396000, 445500, 495000, 594000, 693000, 792000, 891000, 990000],
  [198000, 198000, 264000, 330000, 396000, 462000, 528000, 594000, 660000, 792000, 924000, 1056000, 1188000, 1320000] ],
      IdentityOptionArray: [0.6, 0.6, 0.6, 0.8, 1.0],
      EngOptionArray: [1.0, 1.8, 2.2],
      AgeOptionArray: [1.0, 2.0, 1.875, 1.75, 1.625, 1.5, 1.375, 1.25, 1.125],
      genderOptionArray: [1.0, 1.0, 1.4, 1.4],
      TimeOptionArray: [0, 12000, 10000, 8000, 3000, 0, 0]
    }
  },

  computed: {
    headCount() {
      return 'HEAD_' + this.headCountList[this.headCountIdx]
    },

    headCountLabel() {
      return this.headCountList[this.headCountIdx] + '명'
    },

    // 시안의 datetime-local 위젯 하나로 endDate/endTime 두 값을 함께 표시·입력한다.
    // 실제 계산에 쓰이는 값은 여전히 endDate/endTime 두 필드 그대로다.
    endDateTime: {
      get() {
        return this.endDate && this.endTime ? this.endDate + 'T' + this.endTime : ''
      },
      set(val) {
        if (!val) return
        var parts = val.split('T')
        this.endDate = parts[0]
        if (parts[1]) this.endTime = parts[1].slice(0, 5)
      }
    },

    minDateTime() {
      return this.getDateStr_min + 'T00:00'
    },

    maxDateTime() {
      return this.getDateStr_max + 'T23:59'
    },

    // ServiceOption.vue 의 getDateStr_min 과 동일한 로직
    getDateStr_min() {
      var today = new Date()
      var todayPlus9 = today.setHours(today.getHours() + 9)
      var todayPlus9Date = new Date(todayPlus9)
      var min = todayPlus9Date.toISOString()
      var tmp1 = min.split('T')
      var tmp2 = tmp1[0]
      var dddddd = tmp2.split('-')

      var year = dddddd[0]
      var month = dddddd[1]
      var day = dddddd[2]

      month = month.length == 2 ? month : '0' + month
      day = day.length == 2 ? day : '0' + day
      return year + '-' + month + '-' + day
    },

    // ServiceOption.vue 의 getDateStr_max 와 동일한 로직
    getDateStr_max() {
      var today = new Date()
      var maxD = new Date(today.setDate(today.getDate() + 7))
      var tmp = maxD.setHours(maxD.getHours() + 9)
      var maxT = new Date(tmp)

      var max = maxT.toISOString()
      var split = max.split('T')[0]
      var fullDate = split.split('-')

      var year = fullDate[0]
      var month = fullDate[1]
      var day = fullDate[2]

      month = month.length == 2 ? month : '0' + month
      day = day.length == 2 ? day : '0' + day

      return year + '-' + month + '-' + day
    },

    // ServiceOption.vue 의 timeOptionCal 과 동일한 로직 (마감 구간별 추가요금 인덱스)
    timeOptionCal() {
      var endDateTime = this.endDate + ' ' + this.endTime
      var tmp = new Date(endDateTime).getTime()
      var hourGap = parseInt((tmp - this.nowDate.getTime()) / 3600000)
      var hourOptionIndex = 0

      if (hourGap >= 24 && hourGap < 36) hourOptionIndex = 2
      else if (hourGap >= 36 && hourGap < 48) hourOptionIndex = 3
      else if (hourGap >= 48 && hourGap < 72) hourOptionIndex = 4
      else if (hourGap >= 72) hourOptionIndex = 5
      else if (hourGap < 24) hourOptionIndex = 6

      return hourOptionIndex
    },

    hourGap() {
      var endDateTime = this.endDate + ' ' + this.endTime
      var tmp = new Date(endDateTime).getTime()
      return parseInt((tmp - this.nowDate.getTime()) / 3600000)
    },

    deadlineHintText() {
      if (this.timeOptionCal === 6) {
        return '마감 기한은 최소 24시간 이상부터 선택 가능합니다.'
      }
      var fee = this.TimeOptionArray[this.timeOptionCal]
      var feeText = fee > 0 ? ' · 추가 금액 +' + this.priceToString(fee) + '원' : ''
      return '지금부터 약 ' + this.hourGap + '시간 뒤 마감' + feeText
    },

    // ServiceOption.vue 의 engOptionCal 과 동일한 로직 (응답 수 구간별 영어 설문 배율)
    engOptionCal() {
      let idx = 0
      if (!this.enTarget) idx = 0
      else if ((store.state.maps.headCountMap[this.headCount] <= 3) && this.enTarget) idx = 1
      else if ((store.state.maps.headCountMap[this.headCount] > 3) && this.enTarget) idx = 2
      return idx
    },

    // ServiceOption.vue 의 calOrderPrice 와 동일한 계산식 (할인 전 주문 금액)
    calOrderPrice() {
      var spendTimeIdx = store.state.maps.spendTimeMap[this.spendTime]
      var headCountIdx = store.state.maps.headCountMap[this.headCount]
      var targetGenderIdx = store.state.maps.targetGenderMap[this.targetGender]

      return Math.ceil(parseFloat(parseFloat(this.priceTable[spendTimeIdx][headCountIdx])
        * parseFloat(this.EngOptionArray[this.engOptionCal])
        * parseFloat(this.AgeOptionArray[this.targetAgeOptionList.length])
        * parseFloat(this.genderOptionArray[targetGenderIdx])
        + parseFloat(this.TimeOptionArray[this.timeOptionCal])
      ).toFixed(0) / 10) * 10
    },

    // ServiceOption.vue 의 calculate 와 동일한 계산식 (신분 할인 적용된 결제 금액)
    calculate() {
      var spendTimeIdx = store.state.maps.spendTimeMap[this.spendTime]
      var headCountIdx = store.state.maps.headCountMap[this.headCount]
      var targetGenderIdx = store.state.maps.targetGenderMap[this.targetGender]
      var identityIdx = store.state.maps.identityMap[this.identity]

      return Math.ceil(parseFloat(parseFloat(this.priceTable[spendTimeIdx][headCountIdx])
        * parseFloat(this.IdentityOptionArray[identityIdx])
        * parseFloat(this.EngOptionArray[this.engOptionCal])
        * parseFloat(this.AgeOptionArray[this.targetAgeOptionList.length])
        * parseFloat(this.genderOptionArray[targetGenderIdx])
        + parseFloat(this.TimeOptionArray[this.timeOptionCal])
      ).toFixed(0) / 10) * 10
    },

    // 아래는 계산 로직을 바꾸지 않고, calOrderPrice/calculate 결과를 화면 표시용으로만 쪼갠 값이다.
    extraFee() {
      return this.TimeOptionArray[this.timeOptionCal]
    },

    baseAmount() {
      return this.calOrderPrice - this.extraFee
    },

    // 시안은 "330원 × 3분 × 100명"처럼 단가 곱셈식을 보여주지만, 실제 가격은 구간별
    // 조회표(priceTable)이지 분당 단가 곱셈이 아니라서 그 문구를 그대로 쓰면 다른 구간에서
    // 사실과 다른 계산식으로 보일 수 있다. 대신 조건을 요약한 문구로 대체한다.
    baseAmountNote() {
      var spendTimeLabel = this.spendTimeOptions.find(function (opt) { return opt.value === this.spendTime }, this)
      var label = spendTimeLabel ? spendTimeLabel.label.split(' (')[0] : ''
      return label + ' · ' + this.headCountLabel + ' 기준'
    },

    discountAmount() {
      return this.calOrderPrice - this.calculate
    },

    identityDiscountLabel() {
      var labelMap = { MID_HIGH: '중/고등학생', UNDERGRADUATE: '대학생', GRADUATE: '대학원생', NONE: '일반' }
      var identityIdx = store.state.maps.identityMap[this.identity]
      var percent = Math.round((1 - this.IdentityOptionArray[identityIdx]) * 100)
      return labelMap[this.identity] + ' 할인 ' + percent + '%'
    }
  },

  mounted() {
    this.endDate = this.defaultEndDateStr()
  },

  methods: {
    defaultEndDateStr() {
      var d = new Date()
      d.setDate(d.getDate() + 3)
      var year = d.getFullYear()
      var month = String(d.getMonth() + 1).padStart(2, '0')
      var day = String(d.getDate()).padStart(2, '0')
      return year + '-' + month + '-' + day
    },

    selectAgeAll() {
      this.ageAll = true
      this.targetAgeOptionList = []
    },

    toggleAge(value) {
      this.ageAll = false
      var idx = this.targetAgeOptionList.indexOf(value)
      if (idx === -1) this.targetAgeOptionList.push(value)
      else this.targetAgeOptionList.splice(idx, 1)
    },

    // ServiceOption.vue 의 nextPage 와 동일한 검증·라우팅
    async nextPage() {
      if (this.timeOptionCal === 6) {
        await showAlert('마감 기한은 최소 24시간 이상부터 선택 가능합니다.')
        return
      }
      if (!this.ageAll && this.targetAgeOptionList.length === 0) {
        await showAlert('응답자 연령을 선택해주세요.')
        return
      }

      if (this.$store.state.isLoggedIn) {
        this.saveOptions()
        this.$emit('ordered')
        this.$router.push('/service/inputform')
        return
      }

      const loggedIn = await requireLogin()
      if (loggedIn) {
        this.saveOptions()
        this.$emit('ordered')
        this.$router.push('/service/inputform')
      }
    },

    // ServiceOption.vue 의 saveOptions 와 동일한 커밋 (연령 무관은 ["ALL"] 한 개로 저장)
    saveOptions() {
      var ageList = this.ageAll ? ['ALL'] : this.targetAgeOptionList
      store.commit('saveSurveyOption', {
        headCount: this.headCount,
        spendTime: this.spendTime,
        endDate: this.endDate,
        endTime: this.endTime,
        targetGender: this.targetGender,
        targetAge: ageList,
        targetEng: this.engOptionCal,
        identity: this.identity,
        price: this.calculate,
        priceDiscounted: (this.calOrderPrice - this.calculate)
      })
    },

    priceToString(price) {
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.calc-card {
  width: 100%;
  max-width: 552px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: var(--surface-card);
  border: 1px solid var(--ink-100);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  text-align: left;
  font-family: var(--font-sans);
}

.calc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
}

.calc-body {
  padding: 0 22px 22px;
}

.calc-title {
  font-size: 18px;
  font-weight: var(--font-weight-semibold);
  color: var(--ink-900);
}

.calc-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--green-700);
  background-color: var(--green-100);
  border-radius: 4px;
  padding: 5px 9px;
}

.calc-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--green-500);
}

.calc-field {
  margin-bottom: 18px;
}

.calc-field-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.calc-label {
  display: block;
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
  color: var(--ink-800);
  margin-bottom: 8px;
}

.calc-value {
  font-size: 18px;
  font-weight: var(--font-weight-bold);
  color: var(--ink-900);
}

.calc-slider {
  width: 100%;
  margin-top: 10px;
  accent-color: var(--green-500);
  cursor: pointer;
}

.calc-hint-row {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
}

.calc-hint {
  font-size: 13px;
  color: var(--ink-400);
}

.calc-hint-warn {
  color: var(--danger-500);
  font-weight: var(--font-weight-medium);
}

.calc-datetime {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--ink-200);
  border-radius: var(--radius-md);
  background-color: var(--white);
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--ink-800);
  padding: 10px 12px;
  height: 40px;
  cursor: pointer;
}

.calc-datetime:focus {
  outline: none;
  border-color: var(--green-500);
}

.calc-field-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.calc-select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--ink-200);
  border-radius: var(--radius-md);
  background-color: var(--white);
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--ink-800);
  padding: 10px 12px;
  height: 40px;
  cursor: pointer;
}

.calc-select:focus {
  outline: none;
  border-color: var(--green-500);
}

.age-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.age-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: var(--ink-700);
  cursor: pointer;
}

.age-item input {
  accent-color: var(--green-500);
  cursor: pointer;
}

.calc-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: var(--ink-700);
  margin-bottom: 18px;
  cursor: pointer;
}

.calc-checkbox-row input {
  accent-color: var(--green-500);
  cursor: pointer;
}

.price-breakdown {
  background-color: var(--bg-sunken);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 16px;
}

.price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: var(--font-weight-regular);
  color: var(--ink-800);
  padding: 4px 0;
}

.price-row-note {
  display: block;
  font-size: 12px;
  font-weight: var(--font-weight-regular);
  color: var(--ink-400);
  margin-top: 2px;
}

.price-row-strong {
  font-weight: var(--font-weight-semibold);
}

.price-discount {
  color: var(--green-600);
  font-weight: var(--font-weight-semibold);
}

.price-divider {
  border-top: 1px solid var(--ink-200);
  margin: 8px 0;
}

.price-final {
  font-size: 34px;
  font-weight: var(--font-weight-bold);
  color: var(--green-500);
  flex-wrap: wrap;
  row-gap: 4px;
}

.price-final span {
  word-break: keep-all;
  white-space: nowrap;
}

.calc-order-btn {
  width: 100%;
  box-sizing: border-box;
  height: 48px;
  background-color: var(--green-500);
  color: var(--white);
  border: 1px solid var(--green-500);
  border-radius: var(--radius-md);
  padding: 0 24px;
  font-size: 17px;
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-sans);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.calc-order-btn:hover {
  background-color: var(--green-600);
  border-color: var(--green-600);
}

.calc-order-arrow {
  font-weight: var(--font-weight-regular);
}

.calc-subtext {
  text-align: center;
  font-size: 13px;
  color: var(--ink-400);
  margin-top: 10px;
}

@media (max-width: 480px) {
  .calc-header {
    padding: 14px 18px;
  }

  .calc-body {
    padding: 0 18px 18px;
  }

  .calc-field-grid {
    grid-template-columns: 1fr;
  }

  .price-final {
    font-size: 28px;
  }
}
</style>
